/**
 * Axios global response interceptor.
 *
 * Catches 401 Unauthorized responses from the CC backend (i.e. the session
 * cookie has expired or been invalidated) and immediately resets the Redux
 * auth state to 'logout', which causes LayoutPresentation to redirect to /login.
 *
 * This covers every axios call in the app — both the GraphQL helper and the
 * direct axios.get/post/put/patch calls in EmailTemplates, DripCampaigns, etc.
 *
 * Call setupAxiosInterceptors(store) once at app startup (index.tsx).
 */

import type { Store } from '@reduxjs/toolkit';
import ActionTypes from '@store/features/loginUserProfile/actions';
import axios from 'axios';

// Track whether we've already kicked off a redirect so we don't spam dispatches.
let sessionExpired = false;

export function setupAxiosInterceptors(store: Store) {
    axios.interceptors.response.use(
        // Pass successful responses straight through.
        response => response,

        error => {
            const status = error?.response?.status;

            if (status === 401 && !sessionExpired) {
                sessionExpired = true;

                // Reset auth state — LayoutPresentation will redirect to /login.
                store.dispatch({ type: ActionTypes.LOGOUT_USER_PROFILE_SUCCESS });

                // Allow re-triggering the redirect after a fresh login.
                // Reset the flag once the user has had time to navigate away.
                setTimeout(() => { sessionExpired = false; }, 5000);
            }

            return Promise.reject(error);
        }
    );
}
